import React, { useEffect, useState } from 'react'
import { Container, AvatarCircle } from '../../../../components'
import {
    createStyles,
    makeStyles,
    Theme,
    useTheme,
} from '@material-ui/core/styles'
import {
    Box,
    CircularProgress,
    IconButton,
    LinearProgress,
    Tab,
    Tabs,
    Typography,
} from '@material-ui/core'
import { FileCopyOutlined } from '@material-ui/icons'
import { toast } from 'react-toastify'

import { CircularProgressbarWithChildren } from 'react-circular-progressbar'
import 'react-circular-progressbar/dist/styles.css'
import { axiosInstance, formatPhoneForDisplay } from '../../../../helpers'
import { NVLVendor } from '..'
import {
    Service,
    SimpleVendor,
    VendorDocuments,
    getServiceImageDetails,
} from '../../../../models'
import { Rating } from '@material-ui/lab'
import { typographyStyle } from '../../../../theme/typographyStyle'
import { DocumentPreviewCard } from './DocumentPreviewCard'

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        progressTitle: {
            fontSize: '0.938rem',
            fontWeight: 700,
            lineHeight: '1.7rem',
        },
        progressValue: {
            fontSize: '0.813rem',
            fontWeight: 400,
            color: '#000',
            lineHeight: '1.7rem',
        },
        progress: {
            height: '10px',
            borderRadius: '15px',
        },
        subTitle: {
            fontSize: '1rem',
            fontWeight: 700,
            lineHeight: '1.7rem',
        },
        basicInformation: {
            padding: '16px 0',
            '& span': {
                fontSize: '0.813rem',
                color: '#000',
                lineHeight: '1.6rem',
                display: 'block',
            },
        },
        closeBtn: {
            background: '#FFF',
            color: '#E9190F',
            border: '1px solid #E9190F',
            fontSize: '0.875rem',
            textTransform: 'inherit',
            fontWeight: 500,
            borderRadius: '5px',
            width: '100%',
        },
        //Profile Progress
        progressImage: {
            borderRadius: '100%',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            width: '80%',
            height: '80%',
        },
        workOrderInfo: {
            position: 'relative',
            display: 'block',
            left: '88%',
            width: '85px',
            bottom: '30%',
            fontSize: '12px',
            color: '#b7b7b7',
            fontWeight: 'bold',
            '& h3': {
                margin: '0px',
                fontSize: '18px',
                color: 'rgb(0, 140, 133)',
            },
        },
        indicator: {
            backgroundColor: theme.palette.primary.dark,
        },
    }),
)

export interface VendorWorkorderDetails {
    workorder_count: number
    approved_count: number
    go_back_2_count: number
    go_back_count: number
    services: Service[]
    complete_by_vendor_count: number
}

interface Props {
    selectedVendor: NVLVendor | SimpleVendor
}

const ANALYTICS_TAB = 0
const INFO_TAB = 1
const REVIEWS_TAB = 2

const RATING_INDEX = 0
const DOCUMENT_INDEX = 1
const REVIEWS_INDEX = 3

interface VendorPublicReview {
    id: number
    body: string
    professionalism: number
    work_quality: number
    apartment_name: string | null
    created_at: string
}

const formatReviewDate = (iso: string) => {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) {
        return ''
    }
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

export const VendorDetails = (props: Props) => {
    const { selectedVendor } = props

    const classes = useStyles()
    const theme = useTheme()

    const [rating, setRating] = useState<number | undefined>()

    const [tab, setTab] = useState<number>(ANALYTICS_TAB)

    const [vendorDocuments, setVendorDocuments] = useState<VendorDocuments>({})

    const [
        vendorWorkorderDetails,
        setVendorWorkorderDetails,
    ] = useState<VendorWorkorderDetails>({
        workorder_count: 0,
        approved_count: 0,
        go_back_2_count: 0,
        go_back_count: 0,
        services: [],
        complete_by_vendor_count: 0,
    })

    const [loading, setLoading] = useState(false)
    const [reviews, setReviews] = useState<VendorPublicReview[]>([])

    let borderColor = ''
    let accomplishmentValue = 0

    const tabStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.body1),
        fontWeight: 800,
        textTransform: 'none',
    }

    const infoSectionLabel: React.CSSProperties = {
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: theme.palette.grey[600],
        marginBottom: theme.spacing(1.5),
    }

    switch (true) {
        case vendorWorkorderDetails.workorder_count < 1000: {
            borderColor = theme.palette.silver.main
            accomplishmentValue = 1000
            break
        }
        case vendorWorkorderDetails.workorder_count < 10000: {
            borderColor = theme.palette.gold.main
            accomplishmentValue = 10000
            break
        }
        case vendorWorkorderDetails.workorder_count < 100000: {
            borderColor = theme.palette.blue.main
            accomplishmentValue = 100000
            break
        }
        case vendorWorkorderDetails.workorder_count < 1000000: {
            borderColor = theme.palette.darkBlue.main
            accomplishmentValue = 1000000
            break
        }
        default: {
            borderColor = theme.palette.primary.dark
            accomplishmentValue = 2000000
        }
    }

    useEffect(() => {
        setLoading(true)
        const listRating =
            'rating' in selectedVendor
                ? Number((selectedVendor as { rating?: number }).rating) || 0
                : 0
        if (listRating > 0) {
            setRating(listRating)
        } else {
            setRating(undefined)
        }

        setReviews([])
        const body = {
            vendor_id: selectedVendor.id,
        }
        const ratingPromise = axiosInstance.post(
            `company/national_vendor_list/get_vendor_rating/`,
            body,
        )
        const vendorDocumentPromise = axiosInstance.get(
            `company/national_vendor_list/${selectedVendor.id}/get_vendor_documents/`,
        )
        const vendorDetailsPromise = axiosInstance.post(
            `company/national_vendor_list/get_vendor_detail/`,
            {
                id: selectedVendor.id,
            },
        )
        const vendorReviewsPromise = axiosInstance
            .get(
                `company/national_vendor_list/${selectedVendor.id}/get_vendor_reviews/`,
            )
            .catch(() => ({ data: [] }))

        Promise.all([
            ratingPromise,
            vendorDocumentPromise,
            vendorDetailsPromise,
            vendorReviewsPromise,
        ])
            .then((values) => {
                const apiRating = Number(values[RATING_INDEX].data.rating) || 0
                if (apiRating > 0) {
                    setRating(apiRating)
                } else if (listRating <= 0) {
                    setRating(undefined)
                }
                setVendorDocuments(values[DOCUMENT_INDEX].data)
                setVendorWorkorderDetails(values[2].data)
                const reviewRows = values[REVIEWS_INDEX].data
                setReviews(Array.isArray(reviewRows) ? reviewRows : [])
            })
            .finally(() => setLoading(false))
    }, [selectedVendor])

    const completeCount = selectedVendor.complete_count
    const vendorEmail =
        'email' in selectedVendor
            ? (selectedVendor as NVLVendor).email
            : undefined
    const copyVendorEmail = async () => {
        if (!vendorEmail) {
            return
        }
        try {
            await navigator.clipboard.writeText(vendorEmail)
        } catch {
            const textArea = window.document.createElement('textarea')
            textArea.value = vendorEmail
            window.document.body.appendChild(textArea)
            textArea.select()
            window.document.execCommand('copy')
            window.document.body.removeChild(textArea)
        }
        toast.success('Email copied')
    }
    const sortedServices = [...vendorWorkorderDetails.services].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    )

    const profileAvatar =
        selectedVendor.profile_picture !== null &&
        selectedVendor.profile_picture !== '' ? (
            <img
                src={selectedVendor.profile_picture}
                alt=""
                style={{
                    height: '125px',
                    width: '125px',
                    borderRadius: '50%',
                }}
            />
        ) : (
            <AvatarCircle
                text={selectedVendor.name}
                initials={true}
                styles={{
                    container: {
                        padding: theme.spacing(7.5),
                        fontSize: '40px',
                        backgroundColor: theme.palette.primary.dark,
                        justifyContent: 'center',
                        alignItems: 'center',
                    },
                }}
            />
        )

    if (loading) {
        return (
            <Container justifyContent="center" alignItems="center">
                <CircularProgress size={100} />
            </Container>
        )
    }

    return (
        <Container
            direction="column"
            style={{
                width: '100%',
                padding: theme.spacing(2),
                marginTop: theme.spacing(2),
                overflow: 'auto',
                height: 'calc(90vh - 100px)',
            }}
            flex={1}
        >
            <Container
                style={{
                    width: '100%',
                    gap: theme.spacing(2),
                    alignItems: 'stretch',
                    marginBottom: theme.spacing(1),
                }}
            >
                <Container
                    direction="column"
                    alignItems="center"
                    style={{
                        flex: 1,
                        minWidth: 0,
                        textAlign: 'center',
                    }}
                >
                    {completeCount ? (
                        <div
                            style={{
                                width: 150,
                                height: 150,
                                marginBottom: theme.spacing(1.5),
                            }}
                        >
                            <CircularProgressbarWithChildren
                                value={
                                    (completeCount / accomplishmentValue) * 100
                                }
                                strokeWidth={6}
                                styles={{ path: { stroke: borderColor } }}
                            >
                                {profileAvatar}
                            </CircularProgressbarWithChildren>
                        </div>
                    ) : (
                        <Container
                            justifyContent="center"
                            style={{ marginBottom: theme.spacing(1.5) }}
                        >
                            {profileAvatar}
                        </Container>
                    )}

                    <Typography
                        component="p"
                        style={{
                            fontSize: '1.15rem',
                            fontWeight: 600,
                            lineHeight: '1.35rem',
                            width: '100%',
                        }}
                    >
                        {selectedVendor.name}
                    </Typography>

                    {selectedVendor.phone && (
                        <Container
                            style={{
                                fontWeight: 500,
                                fontSize: 15,
                                color: theme.palette.darkGreen.main,
                                marginTop: theme.spacing(1),
                                justifyContent: 'center',
                            }}
                        >
                            {formatPhoneForDisplay(selectedVendor.phone)}
                        </Container>
                    )}

                    {vendorEmail ? (
                        <Container
                            alignItems="center"
                            style={{
                                fontWeight: 400,
                                fontSize: 13,
                                color: theme.palette.grey[700],
                                marginTop: theme.spacing(0.5),
                                justifyContent: 'center',
                                overflowWrap: 'anywhere',
                                gap: 2,
                            }}
                        >
                            <IconButton
                                size="small"
                                aria-label="Copy email"
                                title="Copy email"
                                onClick={copyVendorEmail}
                                style={{ padding: 4, flexShrink: 0 }}
                            >
                                <FileCopyOutlined
                                    style={{
                                        fontSize: 14,
                                        color: theme.palette.grey[600],
                                    }}
                                />
                            </IconButton>
                            {vendorEmail}
                        </Container>
                    ) : null}

                    {rating ? (
                        <Container
                            justifyContent="center"
                            style={{ marginTop: theme.spacing(1) }}
                        >
                            <Rating
                                readOnly
                                precision={0.5}
                                name={`${selectedVendor.id}_RATING`}
                                value={rating}
                                size="small"
                            />
                        </Container>
                    ) : null}
                </Container>

                <Container
                    direction="column"
                    style={{
                        flex: 1,
                        minWidth: 0,
                        // Match identity column height; scroll when services overflow.
                        maxHeight: 300,
                        overflow: 'hidden',
                    }}
                >
                    <Typography
                        component="p"
                        style={{
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            marginBottom: theme.spacing(1),
                            flexShrink: 0,
                        }}
                    >
                        Services
                    </Typography>
                    <Container
                        direction="column"
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            gap: theme.spacing(1),
                            paddingRight: theme.spacing(0.5),
                        }}
                    >
                        {sortedServices.length === 0 ? (
                            <Container
                                style={{
                                    fontSize: 13,
                                    color: theme.palette.grey[600],
                                }}
                            >
                                No services listed
                            </Container>
                        ) : (
                            sortedServices.map((service) => {
                                const serviceVisual = getServiceImageDetails(
                                    service.id,
                                )
                                const serviceColor =
                                    serviceVisual.color ||
                                    theme.palette.darkGreen.main
                                return (
                                    <Container
                                        key={`VENDOR_${selectedVendor.id}_SERVICE_${service.id}`}
                                        alignItems="center"
                                        style={{
                                            border: `1px solid ${theme.palette.lightGrey.main}`,
                                            borderRadius: 5,
                                            padding: theme.spacing(1, 1.25),
                                            gap: theme.spacing(1),
                                            backgroundColor: '#fff',
                                            backgroundImage: `linear-gradient(${serviceColor}33, ${serviceColor}33)`,
                                            flexShrink: 0,
                                        }}
                                    >
                                        <Container
                                            alignItems="center"
                                            justifyContent="center"
                                            style={{
                                                height: 32,
                                                width: 32,
                                                borderRadius: 6,
                                                backgroundColor: '#fff',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {serviceVisual.icon ? (
                                                <img
                                                    src={serviceVisual.icon}
                                                    alt=""
                                                    style={{
                                                        height: 20,
                                                        width: 20,
                                                        objectFit: 'contain',
                                                    }}
                                                />
                                            ) : (
                                                <span
                                                    style={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor: serviceColor,
                                                    }}
                                                />
                                            )}
                                        </Container>
                                        <span
                                            style={{
                                                fontWeight: 600,
                                                fontSize: 14,
                                                color: '#102A12',
                                                lineHeight: '18px',
                                                minWidth: 0,
                                            }}
                                        >
                                            {service.name}
                                        </span>
                                    </Container>
                                )
                            })
                        )}
                    </Container>
                </Container>
            </Container>

            <Tabs
                style={{
                    color: 'black',
                    borderBottom: `1px solid ${theme.palette.lightGrey.main}`,
                    textTransform: 'none',
                    width: '100%',
                }}
                value={tab}
                onChange={(_, v) => {
                    if (v !== tab) {
                        setTab(v)
                    }
                }}
                classes={{
                    indicator: classes.indicator,
                }}
                variant="fullWidth"
            >
                <Tab label="Analytics" style={{ ...tabStyle }} />
                <Tab label="Info" style={{ ...tabStyle }} />
                <Tab label="Reviews" style={{ ...tabStyle }} />
            </Tabs>

            {tab === ANALYTICS_TAB && (
                <Container direction="column">
                    <Box style={{ padding: '15px 0' }}>
                        {completeCount && (
                            <Box>
                                <Typography
                                    component="p"
                                    className={classes.progressTitle}
                                >
                                    Completed
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={
                                        vendorWorkorderDetails.workorder_count !==
                                        0
                                            ? (completeCount /
                                                  vendorWorkorderDetails.workorder_count) *
                                              100
                                            : 0
                                    }
                                    className={classes.progress}
                                />
                                <Typography
                                    component="p"
                                    className={classes.progressValue}
                                >
                                    {vendorWorkorderDetails.workorder_count !==
                                    0
                                        ? `${(
                                              (completeCount /
                                                  vendorWorkorderDetails.workorder_count) *
                                              100
                                          ).toFixed(2)}%`
                                        : 'No workorders have been assigned '}
                                </Typography>
                            </Box>
                        )}
                        <Box>
                            <Typography
                                component="p"
                                className={classes.progressTitle}
                            >
                                Double Go Backs
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={
                                    vendorWorkorderDetails.workorder_count !== 0
                                        ? (vendorWorkorderDetails.go_back_2_count /
                                              vendorWorkorderDetails.workorder_count) *
                                          100
                                        : 0
                                }
                                className={classes.progress}
                            />
                            <Typography
                                component="p"
                                className={classes.progressValue}
                            >
                                {vendorWorkorderDetails.workorder_count !== 0
                                    ? `${(
                                          (vendorWorkorderDetails.go_back_2_count /
                                              vendorWorkorderDetails.workorder_count) *
                                          100
                                      ).toFixed(2)}%`
                                    : 'No workorders have been assigned '}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                component="p"
                                className={classes.progressTitle}
                            >
                                Go Backs
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={
                                    vendorWorkorderDetails.workorder_count !== 0
                                        ? (vendorWorkorderDetails.go_back_count /
                                              vendorWorkorderDetails.workorder_count) *
                                          100
                                        : 0
                                }
                                className={classes.progress}
                            />
                            <Typography
                                component="p"
                                className={classes.progressValue}
                            >
                                {vendorWorkorderDetails.workorder_count !== 0
                                    ? `${(
                                          (vendorWorkorderDetails.go_back_count /
                                              vendorWorkorderDetails.workorder_count) *
                                          100
                                      ).toFixed(2)}%`
                                    : 'No workorders have been assigned '}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                component="p"
                                className={classes.progressTitle}
                            >
                                Approved
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={
                                    vendorWorkorderDetails.workorder_count !== 0
                                        ? (vendorWorkorderDetails.approved_count /
                                              vendorWorkorderDetails.workorder_count) *
                                          100
                                        : 0
                                }
                                className={classes.progress}
                            />
                            <Typography
                                component="p"
                                className={classes.progressValue}
                            >
                                {vendorWorkorderDetails.workorder_count !== 0
                                    ? `${(
                                          (vendorWorkorderDetails.approved_count /
                                              vendorWorkorderDetails.workorder_count) *
                                          100
                                      ).toFixed(2)}%`
                                    : 'No workorders have been assigned '}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography
                                component="p"
                                className={classes.progressTitle}
                            >
                                Completed By Vendor
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={
                                    completeCount && completeCount !== 0
                                        ? (vendorWorkorderDetails.complete_by_vendor_count /
                                              completeCount) *
                                          100
                                        : 0
                                }
                                className={classes.progress}
                            />
                            <Typography
                                component="p"
                                className={classes.progressValue}
                            >
                                {completeCount && completeCount !== 0
                                    ? `${(
                                          (vendorWorkorderDetails.complete_by_vendor_count /
                                              completeCount) *
                                          100
                                      ).toFixed(2)}% (${
                                          vendorWorkorderDetails.complete_by_vendor_count
                                      } workorders)`
                                    : 'No completed workorders'}
                            </Typography>
                        </Box>
                    </Box>
                </Container>
            )}

            {tab === INFO_TAB && (
                <Container
                    direction="column"
                    style={{
                        padding: theme.spacing(2, 0),
                        gap: theme.spacing(4),
                    }}
                >
                    <Container direction="column">
                        <span style={infoSectionLabel}>About</span>
                        {selectedVendor.description ? (
                            <Typography
                                style={{
                                    fontSize: 15,
                                    fontWeight: 400,
                                    lineHeight: 1.65,
                                    color: theme.palette.grey[900],
                                    whiteSpace: 'pre-wrap',
                                }}
                            >
                                {selectedVendor.description}
                            </Typography>
                        ) : (
                            <Typography
                                style={{
                                    color: theme.palette.grey[600],
                                    fontSize: 14,
                                }}
                            >
                                No about information yet.
                            </Typography>
                        )}
                    </Container>
                    <Container direction="column">
                        <span style={infoSectionLabel}>References</span>
                        {vendorDocuments.reference_1 ||
                        vendorDocuments.reference_2 ||
                        vendorDocuments.reference_3 ? (
                            <Container
                                style={{
                                    gap: theme.spacing(1.5),
                                }}
                            >
                                {vendorDocuments.reference_1 && (
                                    <DocumentPreviewCard
                                        vendorId={selectedVendor.id}
                                        slot="reference_1"
                                        document={vendorDocuments.reference_1}
                                    />
                                )}
                                {vendorDocuments.reference_2 && (
                                    <DocumentPreviewCard
                                        vendorId={selectedVendor.id}
                                        slot="reference_2"
                                        document={vendorDocuments.reference_2}
                                    />
                                )}
                                {vendorDocuments.reference_3 && (
                                    <DocumentPreviewCard
                                        vendorId={selectedVendor.id}
                                        slot="reference_3"
                                        document={vendorDocuments.reference_3}
                                    />
                                )}
                            </Container>
                        ) : (
                            <Typography
                                style={{
                                    color: theme.palette.grey[600],
                                    fontSize: 14,
                                }}
                            >
                                No references uploaded.
                            </Typography>
                        )}
                    </Container>
                    <Container direction="column">
                        <span style={infoSectionLabel}>Insurance</span>
                        {vendorDocuments.insurance_1 ||
                        vendorDocuments.insurance_2 ? (
                            <Container
                                style={{
                                    gap: theme.spacing(1.5),
                                }}
                            >
                                {vendorDocuments.insurance_1 && (
                                    <DocumentPreviewCard
                                        vendorId={selectedVendor.id}
                                        slot="insurance_1"
                                        document={vendorDocuments.insurance_1}
                                    />
                                )}
                                {vendorDocuments.insurance_2 && (
                                    <DocumentPreviewCard
                                        vendorId={selectedVendor.id}
                                        slot="insurance_2"
                                        document={vendorDocuments.insurance_2}
                                    />
                                )}
                            </Container>
                        ) : (
                            <Typography
                                style={{
                                    color: theme.palette.grey[600],
                                    fontSize: 14,
                                }}
                            >
                                No insurance documents uploaded.
                            </Typography>
                        )}
                    </Container>
                </Container>
            )}

            {tab === REVIEWS_TAB && (
                <Container
                    direction="column"
                    style={{
                        padding: theme.spacing(2, 0),
                        gap: theme.spacing(1.5),
                    }}
                >
                    {reviews.length === 0 ? (
                        <Typography
                            style={{
                                color: theme.palette.grey[600],
                                fontSize: 14,
                            }}
                        >
                            No reviews yet.
                        </Typography>
                    ) : (
                        reviews.map((review) => {
                            const dateLabel = formatReviewDate(
                                review.created_at,
                            )
                            const headerParts = [
                                review.apartment_name,
                                dateLabel,
                            ].filter(Boolean)
                            return (
                                <Container
                                    key={review.id}
                                    direction="column"
                                    style={{
                                        padding: theme.spacing(1.5),
                                        border: `1px solid ${theme.palette.grey[300]}`,
                                        borderRadius: 8,
                                        gap: theme.spacing(0.75),
                                    }}
                                >
                                    {headerParts.length > 0 && (
                                        <span
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 600,
                                                color: theme.palette.grey[800],
                                            }}
                                        >
                                            {headerParts.join(' · ')}
                                        </span>
                                    )}
                                    <Container
                                        style={{
                                            flexWrap: 'wrap',
                                            gap: theme.spacing(2),
                                        }}
                                    >
                                        <Container
                                            direction="column"
                                            style={{ gap: 2 }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    color:
                                                        theme.palette.grey[600],
                                                }}
                                            >
                                                Professionalism
                                            </span>
                                            <Rating
                                                value={Number(
                                                    review.professionalism,
                                                )}
                                                precision={0.5}
                                                readOnly
                                                size="small"
                                            />
                                        </Container>
                                        <Container
                                            direction="column"
                                            style={{ gap: 2 }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    color:
                                                        theme.palette.grey[600],
                                                }}
                                            >
                                                Quality of work
                                            </span>
                                            <Rating
                                                value={Number(
                                                    review.work_quality,
                                                )}
                                                precision={0.5}
                                                readOnly
                                                size="small"
                                            />
                                        </Container>
                                    </Container>
                                    <Typography
                                        style={{
                                            fontSize: 14,
                                            whiteSpace: 'pre-wrap',
                                        }}
                                    >
                                        {review.body}
                                    </Typography>
                                </Container>
                            )
                        })
                    )}
                </Container>
            )}
        </Container>
    )
}
